import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calculator, TrendingUp, Users } from 'lucide-react';
import { CONTRACT_PARAMS } from '../ton/config';
import { formatInt, formatTon } from '../lib/format';

const LEVELS = CONTRACT_PARAMS.referralLevels;
const SHARE = CONTRACT_PARAMS.referralSharePercent / 100; // 0.20

/**
 * Commission fraction that reaches an inviter from a buyer `depth` levels below
 * them. The contract takes {@link CONTRACT_PARAMS.referralSharePercent}% of the
 * *remaining* amount at every hop up the chain, so an ancestor at distance d
 * receives SHARE · (1 − SHARE)^(d−1) of the buyer's contribution.
 */
function levelRate(depth: number): number {
  return SHARE * Math.pow(1 - SHARE, depth - 1);
}

interface LevelRow {
  level: number;
  people: number;
  rate: number;
  earnings: number;
}

/**
 * "What could I earn?" estimator for the referral page. The user picks how many
 * people each participant invites (`invites`) and how much each contributes
 * (`contribution`); we project a full {@link LEVELS}-level tree where every node
 * invites the same number, and sum the on-chain commissions that flow up to the
 * user. Purely illustrative — numbers are language-neutral, captions localized.
 */
export function ReferralCalculator() {
  const { t } = useTranslation();
  const [invites, setInvites] = useState(3);
  const [contribution, setContribution] = useState(10);

  const { rows, totalEarnings, networkSize } = useMemo(() => {
    const out: LevelRow[] = [];
    let people = 1; // start at "me"
    let earnings = 0;
    let network = 0;
    for (let level = 1; level <= LEVELS; level += 1) {
      people *= invites; // n^level people at this depth
      network += people;
      const rate = levelRate(level);
      const levelEarnings = people * contribution * rate;
      earnings += levelEarnings;
      out.push({ level, people, rate, earnings: levelEarnings });
    }
    return { rows: out, totalEarnings: earnings, networkSize: network };
  }, [invites, contribution]);

  const maxEarnings = Math.max(...rows.map((r) => r.earnings), 1);

  return (
    <div className="refcalc-card">
      <h3 className="card-head">
        <Calculator size={18} aria-hidden="true" /> {t('referrals.calc.title')}
      </h3>
      <p className="refcalc-sub">{t('referrals.calc.subtitle', { levels: LEVELS })}</p>

      <div className="refcalc-controls">
        <label className="refcalc-control">
          <span className="refcalc-control__label">
            {t('referrals.calc.invitesLabel')}
            <strong className="refcalc-control__value">{invites}</strong>
          </span>
          <input
            type="range"
            min={1}
            max={10}
            step={1}
            value={invites}
            onChange={(e) => setInvites(Number(e.target.value))}
            className="refcalc-slider"
            aria-label={t('referrals.calc.invitesLabel')}
          />
        </label>

        <label className="refcalc-control">
          <span className="refcalc-control__label">
            {t('referrals.calc.contributionLabel')}
            <strong className="refcalc-control__value">{formatTon(contribution)} GRAM</strong>
          </span>
          <input
            type="range"
            min={1}
            max={100}
            step={1}
            value={contribution}
            onChange={(e) => setContribution(Number(e.target.value))}
            className="refcalc-slider"
            aria-label={t('referrals.calc.contributionLabel')}
          />
        </label>
      </div>

      <p className="refcalc-note">{t('referrals.calc.note', { min: CONTRACT_PARAMS.minEntryTon })}</p>

      <div className="refcalc-headline">
        <div className="refcalc-headline__main">
          <span className="refcalc-headline__label">
            <TrendingUp size={14} aria-hidden="true" /> {t('referrals.calc.totalEarnings')}
          </span>
          <span className="refcalc-headline__value">{formatTon(totalEarnings)} GRAM</span>
        </div>
        <div className="refcalc-headline__net">
          <span className="refcalc-headline__label">
            <Users size={14} aria-hidden="true" /> {t('referrals.calc.networkSize')}
          </span>
          <span className="refcalc-headline__net-value">{formatInt(networkSize)}</span>
        </div>
      </div>

      <ol className="refcalc-levels" aria-hidden="false">
        {rows.map((r) => (
          <li key={r.level} className="refcalc-level">
            <span className="refcalc-level__badge">{r.level}</span>
            <div className="refcalc-level__body">
              <div className="refcalc-level__top">
                <span className="refcalc-level__people">
                  {formatInt(r.people)} × {(r.rate * 100).toFixed(r.level === 1 ? 0 : 2)}%
                </span>
                <span className="refcalc-level__earn">{formatTon(r.earnings)} GRAM</span>
              </div>
              <div className="refcalc-level__bar">
                <span
                  className="refcalc-level__fill"
                  style={{ width: `${Math.max(4, (r.earnings / maxEarnings) * 100)}%` }}
                />
              </div>
            </div>
          </li>
        ))}
      </ol>

      <p className="refcalc-disclaimer">{t('referrals.calc.disclaimer')}</p>
    </div>
  );
}
